/**
 * Le sélecteur : un champ compact dans le formulaire, et une fenêtre qui s'ouvre
 * sur la carte. C'est l'objet que rend `create()`.
 */
import { Carte } from './carte'
import { ErreurAcces, SourceGrille, versMaille } from './donnees'
import { texteur, type Texteur } from './textes'
import type { Config, Evenements, Maille, Theme, TypeDeSite, Valeur } from './types'

const TYPES_SITE_DEFAUT: Record<string, TypeDeSite> = {
  AP: { libelle: 'Aire protégée', couleur: '#15803d' },
  TGRN: { libelle: 'Transfert de gestion des ressources naturelles', couleur: '#65a30d' },
  TGRH: { libelle: 'Transfert de gestion des ressources halieutiques', couleur: '#0284c7' },
  LMMA: { libelle: 'Aire marine gérée localement', couleur: '#0891b2' },
}

const FOND_OSM = {
  url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
  maxZoom: 19,
}

const VARIABLES: Record<keyof Theme, string> = {
  accent: '--sgi-accent',
  accentFonce: '--sgi-accent-fonce',
  accentClair: '--sgi-accent-clair',
  texte: '--sgi-texte',
  discret: '--sgi-discret',
  bord: '--sgi-bord',
  fond: '--sgi-fond',
  fondSecondaire: '--sgi-fond-2',
  danger: '--sgi-danger',
  selection: '--sgi-selection',
  rayon: '--sgi-rayon',
  police: '--sgi-police',
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, classe: string, texte?: string): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag)
  if (classe) e.className = classe
  if (texte !== undefined) e.textContent = texte
  return e
}

function bouton(classe: string, texte: string, aria?: string): HTMLButtonElement {
  const b = el('button', classe, texte)
  b.type = 'button'
  if (aria) b.setAttribute('aria-label', aria)
  return b
}

/** Applique le thème (variables CSS) et le mode sombre à un élément racine du module. */
function habiller(e: HTMLElement, config: Config): void {
  for (const [cle, valeur] of Object.entries(config.theme ?? {})) {
    const variable = VARIABLES[cle as keyof Theme]
    if (variable && valeur) e.style.setProperty(variable, valeur)
  }
  if (config.sombre === true) e.classList.add('sgi-sombre')
  if (config.sombre === 'auto') e.classList.add('sgi-sombre-auto')
}

function resumer(m: { code: string | null; id: number; region: string | null }, t: Texteur): string {
  return [m.code ?? `#${m.id}`, m.region ?? t('enMer').replace(/^—\s*/, '')].join(' · ')
}

// ── La fenêtre ────────────────────────────────────────────────────────────────

class Fenetre {
  readonly dialog: HTMLDialogElement
  private readonly corps: HTMLDivElement
  private readonly choix: HTMLSpanElement
  private readonly valider: HTMLButtonElement
  private carte: Carte | null = null
  private enAttente: Maille | null = null
  private conclure: ((m: Maille | null | undefined) => void) | null = null
  private resultat: Maille | null | undefined = undefined

  constructor(
    private readonly config: Config,
    private readonly source: SourceGrille,
    private readonly t: Texteur,
    private readonly lectureSeule: boolean,
    private readonly surErreur: (e: Error) => void,
  ) {
    this.dialog = el('dialog', 'sgi-fenetre')
    habiller(this.dialog, config)
    if (config.largeur) this.dialog.style.setProperty('--sgi-largeur', config.largeur)
    const idTitre = `sgi-titre-${Math.random().toString(36).slice(2, 8)}`
    this.dialog.setAttribute('aria-labelledby', idTitre)

    const entete = el('header', 'sgi-entete')
    const titre = el('h2', 'sgi-titre', config.titre ?? t('titre'))
    titre.id = idTitre
    const fermer = bouton('sgi-fermer', '×', t('fermer'))
    entete.append(titre, fermer)

    this.corps = el('div', 'sgi-corps')
    const pied = el('footer', 'sgi-pied')
    this.choix = el('span', 'sgi-choix')
    const annuler = bouton('sgi-bouton', lectureSeule ? t('fermer') : t('annuler'))
    this.valider = bouton('sgi-bouton sgi-principal', t('valider'))
    this.valider.hidden = lectureSeule
    pied.append(this.choix, annuler, this.valider)
    this.dialog.append(entete, this.corps, pied)
    document.body.appendChild(this.dialog)

    fermer.addEventListener('click', () => this.fermer(undefined))
    annuler.addEventListener('click', () => this.fermer(undefined))
    this.valider.addEventListener('click', () => this.fermer(this.enAttente))
    this.dialog.addEventListener('click', (e) => { if (e.target === this.dialog) this.fermer(undefined) })
    // Échap : traité ici. L'événement « close » est asynchrone, et Chrome ne l'émet
    // pas tant que la page ne se redessine pas : la validation n'en dépend pas.
    this.dialog.addEventListener('cancel', (e) => { e.preventDefault(); this.fermer(undefined) })
    // Filet de sécurité, qui ignore un « close » tardif arrivé après une réouverture.
    this.dialog.addEventListener('close', () => { if (!this.dialog.open) this.terminer() })
  }

  get ouverte(): boolean {
    return this.dialog.open
  }

  /** Rend la maille validée, `null` si « aucune » a été validé, `undefined` si l'utilisateur annule. */
  ouvrir(valeur: number | null, position?: { lat: number; lon: number }): Promise<Maille | null | undefined> {
    if (this.dialog.open) this.fermer(undefined)
    this.resultat = undefined
    this.enAttente = null
    this.majPied()
    document.documentElement.classList.add('sgi-ouverte')
    this.dialog.showModal()
    const carte = this.carteMontee()
    carte.rafraichir()
    if (valeur !== null) void carte.selectionner(valeur, position).catch((e) => this.surErreur(e))
    else carte.effacer(false)
    return new Promise((resolve) => { this.conclure = resolve })
  }

  fermer(resultat: Maille | null | undefined): void {
    this.resultat = resultat
    if (this.dialog.open) this.dialog.close()
    this.terminer()
  }

  detruire(): void {
    this.fermer(undefined)
    this.carte?.detruire()
    this.dialog.remove()
  }

  private terminer(): void {
    document.documentElement.classList.remove('sgi-ouverte')
    const c = this.conclure
    this.conclure = null
    c?.(this.resultat)
  }

  private carteMontee(): Carte {
    if (this.carte) return this.carte
    const racine = el('div', '')
    this.corps.appendChild(racine)
    const c = this.config
    this.carte = new Carte(racine, {
      source: this.source,
      regions: c.couches.regions,
      sites: c.couches.sites,
      champs: { siteType: c.champs?.siteType ?? null, nom: c.champs?.nom ?? 'nom', code: c.champs?.code ?? 'code' },
      fond: c.fond === false ? false : (c.fond ?? FOND_OSM),
      zoomMailles: c.zoomMailles ?? 9,
      hauteur: c.hauteur ?? 'clamp(220px, 46vh, 400px)',
      lectureSeule: this.lectureSeule,
      typesSite: { ...TYPES_SITE_DEFAUT, ...(c.typesSite ?? {}) },
      t: this.t,
      surSelection: (m) => { this.enAttente = m; this.majPied() },
      surErreur: this.surErreur,
    })
    return this.carte
  }

  private majPied(): void {
    const m = this.enAttente
    this.choix.textContent = m ? this.t('selection', { resume: resumer(m, this.t) }) : this.t('aucune')
    this.valider.disabled = m === null
  }
}

// ── Le sélecteur ──────────────────────────────────────────────────────────────

type Ecouteur<K extends keyof Evenements> = (valeur: Evenements[K]) => void

export class Selecteur {
  readonly element: HTMLInputElement
  private readonly t: Texteur
  private readonly lectureSeule: boolean
  private readonly requis: boolean
  /** Valeur au chargement de la page : celle que rend « Réinitialiser ». */
  private readonly initiale: string
  private readonly etatInitial: { type: string; required: boolean }
  private readonly creeParLeModule: boolean
  private readonly champ: HTMLDivElement
  private readonly resume: HTMLSpanElement
  private readonly ouvrirBtn: HTMLButtonElement
  private readonly viderBtn: HTMLButtonElement
  private readonly erreur: HTMLSpanElement
  private fenetre: Fenetre | null = null
  private maille: Maille | null = null
  private position: { lat: number; lon: number } | undefined
  private jeton = 0
  private readonly ecouteurs = new Map<keyof Evenements, Set<Ecouteur<never>>>()
  private readonly surReset = () => setTimeout(() => void this.appliquerValeur(this.initiale, false), 0)
  private readonly surSubmit = (e: Event) => {
    if (this.requis && !this.element.value) {
      e.preventDefault()
      e.stopImmediatePropagation()
      // Carte indisponible : on garde la raison affichée, plus utile que « choisissez ».
      if (this.source) this.signaler(this.t('requis'))
      this.ouvrirBtn.focus()
    }
  }
  /** La grille, une fois ouverte. `null` tant qu'elle ne l'est pas, ou si elle est inaccessible. */
  private source: SourceGrille | null = null

  private constructor(cible: HTMLElement, private readonly config: Config) {
    this.t = texteur(config.langue ?? 'fr', config.textes)
    if (cible instanceof HTMLInputElement) {
      this.element = cible
      this.creeParLeModule = false
    } else {
      this.element = document.createElement('input')
      this.element.name = config.nom ?? 'maille_id'
      cible.appendChild(this.element)
      this.creeParLeModule = true
    }
    const input = this.element
    this.lectureSeule = config.lectureSeule ?? input.readOnly
    this.requis = config.requis ?? input.required
    this.initiale = input.value
    this.etatInitial = { type: input.type, required: input.required }
    const lat = Number(input.dataset.lat), lon = Number(input.dataset.lon)
    if (Number.isFinite(lat) && Number.isFinite(lon) && input.dataset.lat && input.dataset.lon) this.position = { lat, lon }

    // Le champ reste dans le formulaire, qui l'envoie tel quel. `required` est retiré :
    // sur un champ masqué, le navigateur bloquerait l'envoi sans pouvoir montrer où.
    input.type = 'hidden'
    input.required = false

    this.champ = el('div', 'sgi-champ')
    habiller(this.champ, config)
    this.champ.setAttribute('role', 'group')
    this.champ.setAttribute('aria-label', input.getAttribute('aria-label') ?? this.t('libelleChamp'))
    this.resume = el('span', 'sgi-resume')
    this.resume.setAttribute('aria-live', 'polite')
    this.ouvrirBtn = bouton('sgi-bouton sgi-ouvrir', '')
    this.viderBtn = bouton('sgi-vider', '×', this.t('retirer'))
    this.viderBtn.hidden = this.lectureSeule
    this.erreur = el('span', 'sgi-message-erreur')
    this.erreur.hidden = true
    this.champ.append(this.resume, this.ouvrirBtn, this.viderBtn)
    input.insertAdjacentElement('afterend', this.champ)
    this.champ.insertAdjacentElement('afterend', this.erreur)

    this.ouvrirBtn.addEventListener('click', () => void this.open())
    this.viderBtn.addEventListener('click', () => void this.appliquerValeur('', true))
    input.form?.addEventListener('reset', this.surReset)
    input.form?.addEventListener('submit', this.surSubmit, true)
  }

  /**
   * Crée le sélecteur sur un <input> (ou dans un conteneur).
   *
   * Si la grille est inaccessible (clé refusée, adresse erronée), le champ est
   * quand même affiché, en état « Carte indisponible » avec la raison, et la
   * promesse est rejetée : le visiteur voit le problème, le développeur aussi.
   * Sans cela, un champ caché resterait invisible et le formulaire partirait sans maille.
   */
  static async create(cible: HTMLElement, config: Config): Promise<Selecteur> {
    if (!(cible instanceof HTMLElement)) throw new TypeError('create() attend un élément HTML')
    if (!config?.couches?.grille) throw new TypeError('config.couches.grille est obligatoire')
    const s = new Selecteur(cible, config)
    s.resume.textContent = s.t('chargement', { id: s.element.value || '…' })
    s.ouvrirBtn.disabled = true
    try {
      s.source = await SourceGrille.ouvrir(config.couches.grille, config.cle ?? null)
    } catch (e) {
      s.indisponible(e as Error)
      throw e
    }
    await s.afficher()
    s.emettre('ready', undefined)
    return s
  }

  // ── API publique ────────────────────────────────────────────────────────────

  /** Identifiant de la maille du champ, ou `null`. */
  getValue(): number | null {
    const n = Number.parseInt(this.element.value, 10)
    return Number.isInteger(n) && n > 0 ? n : null
  }

  /** La maille du champ, décrite (code, région, sites, centroïde…), ou `null`. */
  getSelection(): Maille | null {
    return this.maille
  }

  /**
   * Change la maille. Avec `{ id, lat, lon }`, la maille est trouvée sans l'index.
   * `emettre` : prévenir le site (événements `change`), comme un choix de l'utilisateur.
   */
  async setValue(valeur: Valeur, options: { emettre?: boolean } = {}): Promise<void> {
    let id = ''
    this.position = undefined
    if (valeur !== null && typeof valeur === 'object') {
      id = String(valeur.id)
      if (Number.isFinite(valeur.lat) && Number.isFinite(valeur.lon)) this.position = { lat: valeur.lat!, lon: valeur.lon! }
    } else if (valeur !== null && valeur !== undefined) id = String(valeur)
    await this.appliquerValeur(id, options.emettre ?? false)
  }

  /** Ouvre la fenêtre. Rend la maille validée, ou `undefined` si l'utilisateur annule. */
  async open(): Promise<Maille | null | undefined> {
    if (!this.source) return undefined
    if (!this.fenetre) {
      this.fenetre = new Fenetre(this.config, this.source, this.t, this.lectureSeule, (e) => this.emettre('error', e))
    }
    this.emettre('open', undefined)
    const choix = await this.fenetre.ouvrir(this.getValue(), this.position)
    this.emettre('close', choix)
    if (!this.lectureSeule && choix !== undefined) {
      this.position = choix ? { lat: choix.centroide[1], lon: choix.centroide[0] } : undefined
      await this.appliquerValeur(choix ? String(choix.id) : '', true, choix)
    }
    this.ouvrirBtn.focus()
    return choix
  }

  close(): void {
    this.fenetre?.fermer(undefined)
  }

  on<K extends keyof Evenements>(evenement: K, ecouteur: Ecouteur<K>): () => void {
    if (!this.ecouteurs.has(evenement)) this.ecouteurs.set(evenement, new Set())
    this.ecouteurs.get(evenement)!.add(ecouteur as Ecouteur<never>)
    return () => this.off(evenement, ecouteur)
  }

  off<K extends keyof Evenements>(evenement: K, ecouteur: Ecouteur<K>): void {
    this.ecouteurs.get(evenement)?.delete(ecouteur as Ecouteur<never>)
  }

  /** Retire le module : le champ d'origine retrouve son type et son `required`. */
  destroy(): void {
    this.fenetre?.detruire()
    this.champ.remove()
    this.erreur.remove()
    this.element.form?.removeEventListener('reset', this.surReset)
    this.element.form?.removeEventListener('submit', this.surSubmit, true)
    if (this.creeParLeModule) this.element.remove()
    else {
      this.element.type = this.etatInitial.type
      this.element.required = this.etatInitial.required
    }
    this.ecouteurs.clear()
  }

  // ── Interne ─────────────────────────────────────────────────────────────────

  private emettre<K extends keyof Evenements>(evenement: K, valeur: Evenements[K]): void {
    for (const f of this.ecouteurs.get(evenement) ?? []) {
      try { (f as Ecouteur<K>)(valeur) } catch (e) { console.error(e) }
    }
  }

  private async appliquerValeur(valeur: string, emettre: boolean, maille: Maille | null = null): Promise<void> {
    const avant = this.element.value
    this.element.value = valeur
    if (valeur === '') this.position = undefined
    this.signaler('')
    await this.afficher(maille)
    if (!emettre || avant === valeur) return
    this.element.dispatchEvent(new Event('input', { bubbles: true }))
    this.element.dispatchEvent(new Event('change', { bubbles: true }))
    this.element.dispatchEvent(new CustomEvent('sgi:change', { bubbles: true, detail: this.maille }))
    this.emettre('change', this.maille)
  }

  /** Carte inaccessible : le champ le dit, et n'offre plus rien à cliquer. */
  private indisponible(e: Error): void {
    this.resume.textContent = this.element.value
      ? `${this.t('indisponible')} — id ${this.element.value}`
      : this.t('indisponible')
    this.resume.classList.add('sgi-vide')
    this.ouvrirBtn.disabled = true
    this.viderBtn.disabled = true
    this.signaler(e instanceof ErreurAcces ? this.t('accesRefuse') : this.t('donneesIntrouvables', { detail: e.message }))
    this.emettre('error', e)
  }

  private signaler(message: string): void {
    this.erreur.textContent = message
    this.erreur.hidden = message === ''
    this.champ.classList.toggle('sgi-invalide', message !== '')
  }

  /** Met le résumé à jour. `connue` évite de relire la maille quand on vient de la choisir. */
  private async afficher(connue: Maille | null = null): Promise<void> {
    if (!this.source) return
    const jeton = ++this.jeton   // une réponse lente ne doit pas écraser une plus récente
    const id = this.getValue()
    this.viderBtn.disabled = id === null
    this.ouvrirBtn.textContent = this.lectureSeule ? this.t('voir') : id === null ? this.t('choisir') : this.t('changer')
    if (id === null) {
      this.maille = null
      this.resume.textContent = this.lectureSeule ? this.t('aucuneLecture') : this.t('aucune')
      this.resume.classList.add('sgi-vide')
      this.ouvrirBtn.disabled = this.lectureSeule
      return
    }
    this.ouvrirBtn.disabled = false
    this.resume.classList.remove('sgi-vide')
    if (connue && connue.id === id) {
      this.maille = connue
    } else {
      this.resume.textContent = this.t('chargement', { id })
      try {
        const trouve = await this.source.parId(id, this.position)
        if (jeton !== this.jeton) return
        this.maille = trouve ? versMaille(trouve.entite, 'api') : null
      } catch (e) {
        if (jeton !== this.jeton) return
        this.maille = null
        if (e instanceof ErreurAcces) {
          this.indisponible(e)
          return
        }
        this.signaler(this.t('donneesIntrouvables', { detail: (e as Error).message }))
        this.emettre('error', e as Error)
        return
      }
    }
    if (this.maille) {
      this.resume.textContent = resumer(this.maille, this.t)
      this.resume.title = `id ${id}`
    } else {
      this.resume.textContent = this.t('inconnue', { id })
      this.signaler(this.t('inconnueDetail', { id }))
    }
  }
}
